import { acceptHMRUpdate, defineStore } from "pinia";
import { markRaw, toRaw } from "vue";
import { pickBy, cloneDeep } from "lodash-es";
import { i18n } from "@/i18n";
import { clampNumber } from "@/helpers/utility/MathHelper";
import { waitUntil } from "@/helpers/utility/Reactivity";
import { ensureInt } from "@/helpers/utility/Type";
import { mapValues } from "@/helpers/utility/CollectionFunctions";
import { splitUid } from "@/helpers/utility/Id";

import Simulation from "@editor/items/simulation/Simulation";
import Impulse from "@editor/items/impulse/Impulse";
import { useSimulationStore } from "@editor/items/simulation/simulations.store";
import { useIncidentStore } from "@editor/items/incident/incidents.store";
import { useScenarioStore } from "@editor/items/scenario/scenarios.store";
import { useActionStore } from "@editor/items/action/actions.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useSimulationMessage } from "@core/ui/simulationMessage.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { addEffects, joinEffects, valueToEffect, expressionToEffect} from "@editor/items/effect/Effect";
import { useEffectStore } from "@editor/items/effect/effects.store";
import { useConditionStore } from "@editor/items/condition/conditions.store";
import { calculateExpression, calculateExpressionSafe } from "@editor/items/expression/expressionFunctions";
import { useElements } from "@editor/items/element/elements";
import { calculateFlowBranches } from "@editor/items/flow/flowBranchFunctions";
import { useFlowStore } from "@editor/items/flow/flows.store";
import { useFlowBranchStore } from "@editor/items/flow/flowBranches.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useSettingsStore } from "@editor/store/settings.store";
import { calculateExpressionValues } from "@editor/items/index/indexFunctions";
import { useRequirementStore } from "@editor/items/requirement/requirements.store";
import { useOverlay } from "@editor/ui/overlayDialog.store";
import { useSimulatedRoundStore } from "./simulationVisualizer";

import type { Uid } from "@/helpers/utility/Id";
import type { ExpressionValue } from "@editor/items/expression/expressionFunctions";
import type { IndexItem } from "@editor/items/index/IndexItem";
import type { ExpressionParseContext } from "@editor/items/effect/Expression";
import type {
  SimulationRoundItem,
  PendingSimulationRoundEffect
} from "@editor/items/simulationRound/SimulationRoundItem";
import type { TargetItem } from "@editor/items/target/TargetItem";
import type { SimulationItem } from "@editor/items/simulation/SimulationItem";
import type { ResourceItem } from "@editor/items/resource/ResourceItem";
import type { IncidentItem } from "@editor/items/incident/IncidentItem";
import type { ActionItem } from "@editor/items/action/ActionItem";
import type { ScenarioItem } from "@editor/items/scenario/ScenarioItem";
import type { BaseEffectItem, EffectItem, JoinedBaseEffectItem } from "@editor/items/effect/EffectItem";
import type { WithRequired } from "@/helpers/utility/Type";
import type { ConditionItem } from "@editor/items/condition/ConditionItem";
import type { ValueItemUid } from "@editor/items/Types";
import type { ActionCostItem } from "@editor/items/action/ActionCostItem";
import type { FlowItem } from "@editor/items/flow/FlowItem";
import type { FlowBranchItem } from "@editor/items/flow/FlowBranchItem";

const newSimulationSettings = {
  title: "",
  description: "",
  actionsActive: true,
  incidentsActive: true,
  momentumsActive: true,
  scenarioId: null as number | null,
  simulationType: null as string | null,
  roundAmount: null as number | null
};

export type NewSimulationSettings = typeof newSimulationSettings;

type RoundImpulses = {
  target: { [targetId: number]: Impulse };
  resource: { [resourceId: number]: Impulse };
};

export interface SimulationQuickInfo {
  active: boolean;
  lastUpdate: number;
  type: "action" | "incident" | "pendingEffect" | "round";
  roundIndex: number;

  x: number;
  y: number;

  // item properties:
  itemId?: number;
  itemType?: string;
  itemRoundIndex?: number;
}

export interface SimulationItemQuickInfo extends SimulationQuickInfo {
  type: "action" | "incident" | "pendingEffect";
  id?: number;

  itemId: number;
  itemType: string;
  itemRoundIndex: number; // required for pending effects (e.g. action activated in round 3, but we're in round 6)
}
export interface SimulationRoundQuickInfo extends SimulationQuickInfo {
  type: "round";
}

export const useSimulationViewStore = defineStore("model/simulationView", {
  state: () => ({
    /**
     * the simulation that is currently beeing simulated
     */
    openSimulationId: null as number | null,

    selectedRoundIndex: -1,

    /**
     *
     */
    lastLoadedGameId: null as number | null,

    docs: {},

    // view impulse effects and causes
    selectedImpulseSource: null as
      | { id: number; type: "action" | "incident" }
      | { id: string; type: "scheduledEffect" }
      | null,
    selectedImpulseSourceRoundIndex: null as number | null,

    // should simulation run to the end?
    autoplayActive: false,

    simulating: false, // is the simulation running NOW?
    visualizing: false, // is the simulation visualizing the results of a round NOW?

    openRoundDrawers: {} as { [roundIndex: string]: boolean },

    animationSpeed: 0.2,

    showIncidentMessages: true,
    showMomentumMessages: true,
    showResourceMessages: true,
    showActionMessages: true,
    showActiveMarks: true,
    showVisualisations: true,

    settingsOpen: false,

    gameImpulses: {} as {
      [roundIndex: string]: RoundImpulses;
    },

    openQuickInfos: {} as {
      [roundIndex: number]: SimulationQuickInfo[];
    },

    quickInfoEffectSources: {
      incident: false,
      cost: true,
      action: true,
      scheduledEffect: true
    },

    // default settings for a new simulation
    newSimulationSettings,

    unfulfilledActionPopup: null as {
      actionId: number;
      maxOccurrencesReached: boolean;
      conditionIds: number[];
    } | null,

    scenarioEndPopup: null as {
      scenarioEndId: number;
    } | null
  }),

  getters: {
    simulation(state): SimulationItem | null {
      if (state.openSimulationId === null) return null;
      return useSimulationStore().items[state.openSimulationId];
    },
    simulator(): Simulation | null {
      if (this.simulation == null) return null;
      const simulationStore = useSimulationStore();
      return markRaw(simulationStore.getInstance(this.simulation.id));
    },
    scenario(): ScenarioItem | null {
      const simulation = this.simulation;
      if (simulation == null) return null;
      if (simulation.scenarioId == null) return null;
      return useScenarioStore().items[simulation.scenarioId];
    },
    actions(): { [actionId: number]: ActionItem } {
      const simulation = this.simulation;
      const actions = useActionStore().items;
      if (!simulation) return actions;

      return pickBy(actions, (action) =>
        simulation.actionIds.includes(action.id)
      );
    },
    incidents(): { [incidentId: number]: IncidentItem } {
      const incidents = useIncidentStore().items;
      const simulation = this.simulation;
      if (!simulation) return incidents;

      return pickBy(incidents, (incident) =>
        simulation.incidentIds.includes(incident.id)
      );
    },
    targetIds(): readonly number[] {
      const simulation = this.simulation;
      if (simulation == null) {
        return useTargetStore().ids;
      }
      return simulation.targetIds.slice();
    },
    targets(): { [targetId: number]: TargetItem } {
      const allTargets = useTargetStore().items;
      if (this.simulation == null) {
        return allTargets;
      }
      const targetIds = this.targetIds;
      return pickBy(allTargets, (target) => targetIds.includes(target.id));
    },

    resourceIds(): readonly number[] {
      const simulation = this.simulation;
      if (simulation == null) {
        return useResourceStore().ids;
      }
      return simulation.resourceIds.slice();
    },
    resources(): { [resourceId: number]: ResourceItem } {
      const allResources = useResourceStore().items;
      if (this.simulation == null) {
        return allResources;
      }
      const resourceIds = this.resourceIds;
      return pickBy(allResources, (resource) =>
        resourceIds.includes(resource.id)
      );
    },

    indexIds(): readonly number[] {
      const simulation = this.simulation;
      if (simulation == null) {
        return useIndexStore().ids;
      }
      return simulation.indexIds.slice();
    },
    indices(): { [indexId: number]: IndexItem } {
      const allIndices = useIndexStore().items;
      if (this.simulation == null) {
        return allIndices;
      }
      const indexIds = this.indexIds;
      return pickBy(allIndices, (index) => indexIds.includes(index.id));
    },

    // ==================== //
    // ====== ROUNDS =======//
    // ======== //

    currentRoundIndex(): number {
      return this.simulation?.currentRoundIndex ?? -1;
    },

    /**
     * Get the rounds of this simulation.
     */
    rounds(): SimulationRoundItem[] {
      if (this.visualizing) {
        // While we're visualizing, only provide the rounds
        // up to the current selected round.
        // This ensures that the last round is always
        // seen as the current round.
        return (
          this.simulation?.rounds.filter(
            (r) => r.index <= this.selectedRoundIndex
          ) ?? []
        );
      }
      return this.simulation?.rounds ?? [];
    },
    previousRound(state): SimulationRoundItem | null {
      if (this.simulation == null) return null;
      if (state.selectedRoundIndex == null) return null;
      if (state.selectedRoundIndex <= 0) return null;
      return this.rounds[state.selectedRoundIndex - 1];
    },
    selectedRound(): SimulationRoundItem | null {
      if (this.simulation == null) return null;
      const rounds = this.rounds;
      if (this.selectedRoundIndex == -1) {
        return this.simulator?.virtualGameStartRound || null;
      }
      if (rounds.length == 0) return null;
      if (this.selectedRoundIndex == -2) {
        const lastRound = cloneDeep(rounds?.[rounds.length - 1]);
        lastRound.startElementValues = lastRound.elementValues;
        lastRound.startRandomValues = lastRound.randomValues;
        return lastRound;
      }
      return rounds?.[this.selectedRoundIndex] ?? null;
    },
    currentRound(): SimulationRoundItem | null {
      if (this.simulation == null) return null;
      const rounds = this.rounds;
      return rounds?.[this.currentRoundIndex] ?? null;
    },
    previousRoundStartState(): {
      elementValues: { [uid: string]: ExpressionValue };
      actionCounts: { [actionId: number]: number };
      incidentCounts: { [incidentId: number]: number };
      index: number;
    } | null {
      const roundIndex = this.selectedRoundIndex;
      const rounds = this.rounds;
      const simulation = this.simulation;
      if (simulation == null) return null;
      // value evaluation happens at the end of each round.
      // we compare to the round before it because of that.
      if (roundIndex >= 2) {
        return rounds[roundIndex - 2];
      } else {
        return {
          elementValues: cloneDeep(simulation.startElementValues),
          actionCounts: {},
          incidentCounts: {},
          index: 0
        };
      }
    },

    // ===== IMPULSES ======= //

    impulses(state): {
      [roundIndex: string]: RoundImpulses;
    } {
      if (this.simulation == null) return {};
      return state.gameImpulses;
    },
    currentRoundImpulses(): RoundImpulses | null {
      if (this.selectedRound == null) return null;
      const roundIndex = this.selectedRoundIndex;
      const roundImpulses = this.impulses[roundIndex];
      return roundImpulses || { target: {}, resource: {} };
    },

    openRoundImpulses(): RoundImpulses {
      const simulation = this.simulation;

      if (this.simulation == null) {
        return { target: [], resource: [] };
      }

      if (simulation?.complete) {
        return this.selectedRoundImpulses;
      }

      if (this.visualizing) {
        return useSimulatedRoundStore().impulses;
      }

      if (
        this.currentRoundImpulses != null && this.selectedRoundIndex === this.currentRoundIndex
      ) {
        return this.currentRoundImpulses;
      }

      return this.selectedRoundImpulses;
    },
    selectedRoundImpulses(): RoundImpulses {
      const roundIndex = this.selectedRoundIndex ?? -1;

      const roundImpulses = this.impulses?.[roundIndex];
      return roundImpulses || { target: {}, resource: {} };
    },
    openResourceImpulses(): { [impulseId: number]: Impulse } | null {
      const currentRoundImpulses = this.currentRoundImpulses;
      if (currentRoundImpulses == null) return null;
      const resourceImpulses = currentRoundImpulses.resource || {};
      const openResourceImpulses = pickBy(
        resourceImpulses,
        (impulse) => impulse.valueAfter == null
      );
      return openResourceImpulses;
    },

    actionsByRound(): ActionItem[][] {
      const simulation = this.simulation;
      if (simulation == null) return [];

      const actionsByRound = [];
      const maxRounds = simulation?.maxRounds ?? 0;
      for (let i = 0; i < maxRounds; i++) {
        const roundActionPlan = simulation.actionPlans[i];
        if (roundActionPlan == null) {
          actionsByRound.push([]);
          continue;
        }
        actionsByRound.push(
          roundActionPlan.plannedActionIds.map(
            (actionId) => this.actions[actionId]
          )
        );
      }
      return actionsByRound;
    },
    incidentsByRound(): IncidentItem[][] {
      const simulation = this.simulation;
      if (simulation == null) return [];

      const incidentsByRound = [];
      const maxRounds = simulation?.maxRounds ?? 0;
      for (let i = 0; i < maxRounds; i++) {
        const round = simulation.rounds[i];
        if (round == null) {
          incidentsByRound.push([]);
          continue;
        }
        incidentsByRound.push(
          round.activatedIncidents.map(
            (incidentReason) => this.incidents[incidentReason.incidentId]
          )
        );
      }
      return incidentsByRound;
    },

    pendingEffectsByRound(): {
      [roundIndex: string]: PendingSimulationRoundEffect[];
    } {
      const simulation = this.simulation;
      if (simulation?.id == null) return {};
      return useSimulationStore().getPendingEffectsByRound(simulation?.id);
    },

    /**
     * Get combined all open effects of the current round, added, by net item.
     * Open effects are effects that have not been added to the simulation impulses by now.
     */
    getOpenRoundEffects(): (
      roundIndex: number,
      activeTypes?: (
        | "incident"
        | "scheduledEffect"
        | "action"
        | "cost"
        | "pendingEffect"
      )[]
    ) => {
      target: { [targetId: number]: BaseEffectItem[] };
      resource: { [resourceId: number]: BaseEffectItem[] };
    } {
      return (
        roundIndex: number,
        activeTypes: (
          | "incident"
          | "scheduledEffect"
          | "action"
          | "cost"
          | "pendingEffect"
        )[] = ["scheduledEffect", "action"]
      ) => {
        const simulation = this.simulation;
        if (simulation == null) {
          return { target: {}, resource: {} };
        }

        const allEffects = [];

        if (activeTypes.includes("incident")) {
          const round = this.rounds[roundIndex];
          if (round) {
            const incidentIds = round.activatedIncidents.map(
              (i) => i.incidentId
            );
            const roundIncidents =
              useIncidentStore().getArrayByIds(incidentIds);
            const incidentEffects = roundIncidents.flatMap((incident) =>
              useEffectStore().getByActivator(incident)
            );

            const activatedIncidentEffects = incidentEffects
              .map((effect) => {
                const impulse =
                  round.impulses[effect.affectedType]?.[effect.affectedId];
                if (impulse == null) return null;
                const subImpulse = impulse.sourceValues.find(
                  (sourceValue) =>
                    sourceValue.type == effect.activatorType &&
                    sourceValue.id == effect.activatorId
                );
                if (subImpulse == null) return null;
                return valueToEffect(subImpulse.value || 0, effect);
              })
              .filter((e) => e != null) as EffectItem[];

            allEffects.push(...activatedIncidentEffects);
          }
        }

        const actionPlan = useSimulationStore().getActionPlan(
          simulation.id,
          roundIndex
        );

        if (activeTypes.includes("action")) {
          let plannedActionIds = actionPlan?.plannedActionIds ?? [];

          if (roundIndex == this.currentRoundIndex) {
            // remove the actions if they are not playable
            const actionsPlayableState = this.currentRoundActionsPlayable;
            plannedActionIds = plannedActionIds.filter(
              (id) => actionsPlayableState[id] == true
            );
          }

          const roundActions =
            useActionStore().getArrayByIds(plannedActionIds);
          const actionEffects = roundActions.flatMap((action) =>
            useEffectStore().getByActivator(action)
          );
          allEffects.push(...actionEffects);

          if (activeTypes.includes("cost")) {
            const costsAsEffects = roundActions.flatMap((action) =>
              action.costs.map((cost) => {
                const { type, id } = splitUid(cost.elementUid);
                return expressionToEffect(cost.expression, {
                  activatorType: "action",
                  activatorId: action.id,
                  affectedType: type,
                  affectedId: id
                });
              })
            );
            allEffects.push(...costsAsEffects);
          }
        }

        if (activeTypes.includes("pendingEffect")) {
          const pendingEffects = (this.pendingEffectsByRound[roundIndex] ?? []).flatMap(
            (e) => e.effects
          );
          allEffects.push(...pendingEffects);
        }

        if (activeTypes.includes("scheduledEffect")) {
          allEffects.push(...actionPlan.scheduledEffects);
        }

        const effectsByAffected: {
          target: { [targetId: number]: BaseEffectItem[] };
          resource: { [resourceId: number]: BaseEffectItem[] };
        } = { target: {}, resource: {} };

        allEffects.reduce((list, effect) => {
          const typeList = list[effect.affectedType];
          if (typeList == null) {
            console.error(
              `Effect ${effect.id} has an unknown affected type: ${effect.affectedType}`
            );
            return list;
          }
          const key: number = effect.affectedId;
          if (effect.visibility == "hidden") {
            return list;
          }
          
          if (!typeList[key]) typeList[key] = [];
          typeList[key].push(effect);
          return list;
        }, effectsByAffected);

        return effectsByAffected;
      };
    },
    getJoinedOpenRoundEffects(): (
      roundIndex: number,
      activeTypes?: (
        | "incident"
        | "scheduledEffect"
        | "action"
        | "cost"
        | "pendingEffect"
      )[]
    ) => { [uid: string]: JoinedBaseEffectItem } {
      return (
        roundIndex: number,
        activeTypes: (
          | "incident"
          | "scheduledEffect"
          | "action"
          | "cost"
          | "pendingEffect"
        )[] = ["scheduledEffect", "action"]
      ) => {
        const incidents = useIncidentStore().items;
        const actions = useActionStore().items;
        const activators = {
          incident: incidents,
          action: actions,
          scheduledEffect: {} as Record<number, never>
        };
        const roundEffects = this.getOpenRoundEffects(roundIndex, activeTypes);
        const joinedEffects: { [uid: `${'target' | 'resource'}_${number}`]: JoinedBaseEffectItem } = {};
        for (const _type in roundEffects) {
          const type = _type as "target" | "resource";
          for (const _id in roundEffects[type]) {
            const id = parseInt(_id);

            const effects = (roundEffects[type][id] || []).map((effect): BaseEffectItem => {
              if (effect.id == null || effect.id == -1) return effect; // for filtering out cost effects
              if (effect.activatorId == null) return effect;
              
              // inherit the visibility from the activator
              const activator = activators[effect.activatorType]?.[ensureInt(effect.activatorId)];
              if (activator && activator.effectsVisibility != "as-is") {
                let visibility = effect.visibility;
                if (activator.effectsVisibility == "values-hidden") visibility = "hint";
                if (activator.effectsVisibility == "hidden") visibility = "hidden";
                if (activator.effectsVisibility == "visible") visibility = "visible";
                return {
                  ...effect,
                  visibility
                };
              }
              return effect;
            });
            
            let joinedEffect: JoinedBaseEffectItem;
            const firstEffect = joinEffects(effects[0], null);
            if (effects.length == 1) {
              joinedEffect = firstEffect
            } else {
              joinedEffect = effects.slice(1).reduce((joinedEffect, effect): JoinedBaseEffectItem => {
                return joinEffects(joinedEffect, effect);
              }, firstEffect);
            }

            joinedEffects[`${type}_${id}`] = joinedEffect;
          }
        }
        return joinedEffects;
      };
    },

    currentRoundActionsRequirementFulfilled(): {
      [actionId: number]: boolean;
    } {
      const currentRound = this.currentRound;
      if (currentRound == null) return {};
      if (currentRound.played) return {};

      const actions = this.actions;

      const previousRoundStartState = this.previousRoundStartState;
      if (!previousRoundStartState) return {};

      return mapValues(actions, (action: ActionItem) => {
        const requirements = useRequirementStore().byItemUid[action.uid] || [];
        // no requirements, no fulfillment needed
        if (requirements.length == 0) return true;

        const conditionStore = useConditionStore();
        const requirementConditions = requirements.reduce((conditionsByRequirement, requirement) => {
          const conditions = conditionStore.getBySource(requirement);
          conditionsByRequirement[requirement.id] = conditions;
          return conditionsByRequirement;
        }, {} as { [requirementId: number]: ConditionItem[] });

        const validRequirements = requirements.filter((requirement) => {
          return requirementConditions[requirement.id].length > 0;
        });
        // if there are no requirements, the action is always fulfilled
        if (validRequirements.length == 0) return true;

        const fulfilled = validRequirements.some((requirement) => {
          const conditions = conditionStore.getBySource(requirement);
          return conditions.every((condition) =>
            conditionStore.evaluate(condition.id, this.rounds)
          );
        });
        return fulfilled;
      });
    },
    currentRoundActionUsesLeftStates(): {
      [actionId: number]: boolean;
    } {
      const currentRound = this.currentRound;
      if (currentRound == null) return {};
      if (currentRound.played) return {};

      const actions = this.actions;

      return mapValues(actions, (action: ActionItem) => {
        const plays = currentRound.actionCounts[action.id];
        if (
          plays != null &&
          action.maxOccurrences !== null &&
          plays >= action.maxOccurrences
        ) {
          return false;
        }
        return true;
      });
    },
    currentRoundActionsPlayable(): {
      [actionId: number]: boolean;
    } {
      const currentRound = this.currentRound;
      if (currentRound == null) return {};
      if (currentRound.played) return {};

      const actions = this.actions;

      const previousRoundStartState = this.previousRoundStartState;
      if (!previousRoundStartState) return {};

      return mapValues(actions, (action: ActionItem) => {
        // check if the action is already used up
        if (this.currentRoundActionUsesLeftStates[action.id] == false) {
          return false;
        }

        // check if any requirement is fulfilled
        if (this.currentRoundActionsRequirementFulfilled[action.id] == false) {
          return false;
        }

        // check if all costs are available
        if (this.currentRoundActionsPayable[action.id] == false) {
          return false;
        }

        return true;
      });
    },

    currentRoundActionCostValues(): { [actionId: number]: (ActionCostItem & { value: ExpressionValue })[]} {
      const currentRound = this.currentRound;
      if (currentRound == null) return {};
      if (currentRound.played) return {};

      const actions = this.actions;

      const previousRoundStartState = this.previousRoundStartState;
      if (!previousRoundStartState) return {};

      const parseContext = {
        ...this.parseContext,
        rounds: this.rounds,
        roundIndex: currentRound.index
      };

      return mapValues(actions, (action: ActionItem) => {
        return action.costs.map((cost): (ActionCostItem & { value: ExpressionValue }) => {
          const calculated = calculateExpressionSafe(cost.expression, parseContext);
  
          return {
            ...cost,
            value: calculated
          };
        });
      });
    },

    currentRoundActionsPayable(): { [actionId: number]: boolean } {
      const currentRound = this.currentRound;
      if (currentRound == null) return {};
      if (currentRound.played) return {};

      const allActions = Object.values(this.actions);

      const currentRoundActions = this.actionsByRound[currentRound.index];
      const selectedActionIds = new Set(currentRoundActions.map((action) => action.id));
      const remainingActions = allActions.filter((action) => {
        return !selectedActionIds.has(action.id);
      });
      const actions = [...currentRoundActions, ...remainingActions];

      const availableElementValues = cloneDeep(currentRound.elementValues);

      const actionsPayable: { [actionId: number]: boolean } = {};

      actions.forEach((action: ActionItem) => {
        let payable = true;

        const costs = this.currentRoundActionCostValues[action.id] ?? [];

        // check if all costs are available
        for (const cost of costs) {
          if (!cost.value.valid) continue;
          const costAmount = Number(cost.value.value);
          if (costAmount == null) continue;

          const uid = cost.elementUid;
          const group = availableElementValues[uid];

          if (group == null) continue;

          if (costAmount < 0) {
            const minValue = group.minValue;
            const availableValue = minValue == null ? Infinity : Number(group.value) - minValue;
  
            // TODO: add check for uncertainity?
            const requiredValue = Math.abs(costAmount);
  
            if (availableValue < requiredValue) {
              payable = false;
              break;
            }
          } else {
            const maxValue = group.maxValue;
            const availableValue = maxValue == null ? Infinity : (maxValue - Number(group.value));

            const requiredValue = costAmount;

            if (availableValue < requiredValue) {
              payable = false;
              break;
            }
          }
        }

        actionsPayable[action.id] = payable;
      });

      return actionsPayable;
    },

    elementValues(): { [uid: string]: ExpressionValue } {
      if (!this.simulation) {
        return useElements().elementValues;
      }

      const selectedRound = cloneDeep(this.selectedRound);
      if (selectedRound == null) {
        return this.simulation.startElementValues;
      }

      if (this.visualizing) {
        return useSimulatedRoundStore().elementValues;
      }

      if (
        selectedRound.played ||
        this.currentRoundIndex != selectedRound.index
      ) {
        // if the round has been played, we can return the values as they are.
        return selectedRound.startElementValues;
      }

      // Calculate the values for the current round
      // The current round is half-played, so we're applying the effects
      // of the actions that have been played so far.

      const elementValues = cloneDeep(selectedRound.startElementValues);

      // Predict the values of the flows with all planned effects added up:
      return withCalculatedFlowBranches(
        elementValues,
        this.parseContext,
        this.impulses[selectedRound.index],
        this.getOpenRoundEffects(selectedRound.index, [
          // no incidents, because they are converted to impulses already.
          "cost",
          "action",
          "pendingEffect",
          "scheduledEffect"
        ])
      );
    },

    parseContext(): WithRequired<ExpressionParseContext, "indices" | "targets" | "resources"> {
      const settings = useSettingsStore();
      return {
        targets: useTargetStore().items,
        resources: useResourceStore().items,
        indices: useIndexStore().items,
        roundIndex: this.currentRoundIndex,
        rounds: [...this.rounds],
        randomValues: this.currentRound?.startRandomValues || {},
        simulationSettings: {
          roundUnit: this.simulation?.roundUnit || settings.simulationRoundUnit,
          roundLength:
            this.simulation?.roundLength || settings.simulationRoundLength,
          durationType:
            this.simulation?.durationType || settings.simulationDurationType,
          timeStart: this.simulation?.timeStart || settings.simulationTimeStart
        }
      };
    },

    selectedRoundActionCosts(): {
      [elementUid: string]: { value: number; actionId: number }[];
    } {
      if (this.simulation == null) return {};
      if (this.selectedRound == null) return {};
      const costByElement: {
        [elementUid: string]: { value: number; actionId: number }[];
      } = {};

      const parseContext = this.parseContext;

      if (this.selectedRound.played) {
        const activatedActions = this.selectedRound.activatedActions;
        const costs = activatedActions.reduce((costByElement, activatedAction) => {
          for (const elementUid in activatedAction.costs) {
            if (!costByElement[elementUid]) costByElement[elementUid] = [];
            costByElement[elementUid].push({
              value: activatedAction.costs[elementUid],
              actionId: activatedAction.actionId
            });
          }
          return costByElement;
        }, costByElement);
        return costs;
      } else {
        const actions = this.actionsByRound[this.selectedRound.index] || [];
        const actionsPlayable = this.currentRoundActionsPlayable;
        const costs = actions.reduce((costByElement, action) => {
          if (actionsPlayable[action.id] == false) return costByElement;

          for (const cost of action.costs) {
            if (!costByElement[cost.elementUid]) costByElement[cost.elementUid] = [];
            const calculated = calculateExpressionSafe(cost.expression, parseContext);
            const value = Number(calculated.value);
            costByElement[cost.elementUid].push({
              value: value,
              actionId: action.id
            });
          }
          return costByElement;
        }, costByElement);
        return costs;
      }
    }
  },

  actions: {
    /**
     * Simulate the next round, updating the simulator instance, but without visualizing the rounds.
     */
    async simulateRound(): Promise<Simulation> {
      
      const simulationStore = useSimulationStore();
      const simulator = this.simulator;
      
      if (simulator == null) {
        throw new Error("No simulation loaded");
      }

      if (simulator.complete) {
        // don't simulate if the simulation is already complete
        return simulator;
      }

      this.simulating = true;

      const roundIndex = this.currentRoundIndex || 0;

      // add the actions aligned for the simulation to the simulator
      for (let i = roundIndex; i < simulator.maxRounds; i++) {
        const roundActionPlan = simulationStore.getActionPlan(
          simulator.id,
          i
        );
        simulator.updateActionPlan(i, roundActionPlan);
      }

      // use the simulation class to simulate the round.
      // the results can be used afterwards to show what happened.
      simulator.simulateRound();

      if (simulator.complete) {
        this.autoplayActive = false;
      }

      this.simulating = false;

      // INVESTIGATE: Why did we save the simulation after visualizing the first half before? 
      // We should be able to save it right after simulating. What could be influencing this?
      await this.updateImpulses(markRaw(simulator.impulses));
      const simulationState: SimulationItem = cloneDeep(cloneWithoutGetters(toRaw(simulator))) as SimulationItem;
      console.log("save simulation state", simulationState);
      await this.saveSimulation(simulationState);

      return simulator;
    },

    updateImpulses(impulses: Simulation["impulses"]): void {
      this.gameImpulses = markRaw(impulses);
    },

    async visualizeRound(roundIndex: number): Promise<void> {
      console.log("visualize round", roundIndex);
      if (this.simulator == null) {
        throw new Error("No simulation loaded");
      }

      if (!this.showVisualisations) {
        this.selectedRoundIndex = this.simulator.currentRoundIndex;

        if (this.simulator.complete) {
          this.selectedRoundIndex = -2;
        }
        return;
      }

      // Set the visualizing flag, so that the UI can react to it (e.g. by hiding certain elements or showing loading spinners).
      this.visualizing = true;

      // close all open Quick info popups
      this.openQuickInfos = {};

      // close info popups
      useOverlay().close();

      const roundIndexBefore = roundIndex || 0;

      const roundVisualization = useSimulatedRoundStore();
      roundVisualization.visualizationSettings.visibleIncidentMessages = this.showIncidentMessages;
      roundVisualization.visualizationSettings.visibleActionMessages = this.showActionMessages;
      roundVisualization.visualizationSettings.visibleMomentumMessages = this.showMomentumMessages;
      roundVisualization.visualizationSettings.animationSpeed = this.animationSpeed;

      // visualize what happened up to the round change
      const simulatedRound = this.simulator.rounds.find((r) => r.index == roundIndexBefore);
      if (simulatedRound) {
        roundVisualization.loadRound(simulatedRound, this.simulator);
        await roundVisualization.visualizeSecondHalf(this.simulator);
      }

      // await this.saveSimulation(simulator);

      // update round index
      this.selectedRoundIndex = roundIndexBefore + 1;

      // stop autoplay if simulation ended
      if (this.simulator.complete && this.selectedRoundIndex >= this.simulator.currentRoundIndex) {
        this.selectedRoundIndex = -2;

        roundVisualization.showEndCard(this.simulator);
      } else {
        // visualize everything after the round change
        roundVisualization.loadRound(this.simulator.currentRound, this.simulator);
        roundVisualization.resetImpulses();
        await roundVisualization.visualizeFirstHalf(this.simulator);
      }

      this.visualizing = false;
    },

    /**
     * Simulate a standard simulation round.
     * We're starting in the middle of a round, when actions have been defined.
     */
    async simulateAndVisualizeRound(): Promise<void> {
      await this.simulateRound();
      if (this.simulator == null) {
        throw new Error("No simulation loaded");
      }
      const roundIndex = this.simulator.currentRoundIndex;
      await this.visualizeRound(roundIndex - 1);
    },

    /**
     */
    async saveSimulation(simulator: Simulation | SimulationItem) {
      useSimulationStore().saveSimulationState(simulator);
    },

    // ==== ROUND CONTROL ==== //
    /**
     *
     */
    goToRound(roundIndex: number): void {
      if (roundIndex == null) roundIndex = -1;
      
      if (roundIndex == -1) {
        this.selectedRoundIndex = -1;
        return;
      }

      if (roundIndex == -2) {
        if (this.simulation?.complete) {
          this.selectedRoundIndex = -2;
          return;
        }
        roundIndex = this.currentRoundIndex;
      }

      // if the simulation ended, only move up to the last completed round
      if (this.simulation?.complete && roundIndex > this.currentRoundIndex) {
        return;
      }

      const round = this.rounds[roundIndex];
      if (round == null) return;

      // only move up to the next simulatable round.
      if (roundIndex > this.currentRoundIndex) {
        return this.goToRound(this.currentRoundIndex);
      }

      this.selectedRoundIndex = roundIndex;
    },

    /**
     *
     */
    loadSimulation(options: {
      simulationId: number | null;
      roundIndex?: number | null;
    }) {
      if (options.simulationId != this.openSimulationId) {
        this.openQuickInfos = {};
        this.openRoundDrawers = {};
      }

      this.openSimulationId = options.simulationId;

      if (options.simulationId != null) {
        this.lastLoadedGameId = options.simulationId;
      }

      if (this.simulation == null) return null;

      if (options.roundIndex != null) {
        if (options.roundIndex > this.simulation.currentRoundIndex) {
          this.selectedRoundIndex = this.simulation.currentRoundIndex;
        } else {
          this.selectedRoundIndex = options.roundIndex;
        }
      } else {
        this.selectedRoundIndex = this.simulation.currentRoundIndex;
      }

      if (this.simulator == null) {
        console.error("No simulator instance found");
        return;
      }

      this.gameImpulses = markRaw(this.simulator.impulses);

      // show start message if there's one:
      const scenario = this.scenario;
      const isBeforeFirstRound = this.selectedRoundIndex == -1;
      if (
        !this.simulation.complete &&
        isBeforeFirstRound &&
        scenario &&
        scenario.showStartMessage &&
        (scenario.startText || scenario.startTitle)
      ) {
        return useSimulationMessage()
          .show({
            type: scenario.title,
            title: scenario.startTitle,
            buttonText: i18n.t("actions.start"),
            item: {
              type: "simulationStart",
              text: scenario.startText || "",
              imageId: scenario.startImageId
            }
          })
          .then(() => {
            return this.simulateAndVisualizeRound();
          });
      }
    },
    unloadSimulation() {
      this.loadSimulation({
        simulationId: null
      });
    },
    loadLastSimulation() {
      if (this.lastLoadedGameId != null) {
        return this.loadSimulation({
          simulationId: this.lastLoadedGameId
        });
      } else {
        const simulationStore = useSimulationStore();
        const simulations = simulationStore.items;
        const simulationIds = Object.keys(simulations).map(ensureInt);
        if (simulationIds.length == 0) {
          console.error("There's no simulation to load.");
          return;
        }
        const firstGame = simulations[simulationIds[0]];
        return this.loadSimulation({
          simulationId: firstGame.id
        });
      }
    },

    setShowIncidentMessages(val: boolean) {
      this.showIncidentMessages = val;
      useSimulatedRoundStore().visualizationSettings.visibleIncidentMessages = val;
    },
    setShowActionMessages(val: boolean) {
      this.showActionMessages = val;
      useSimulatedRoundStore().visualizationSettings.visibleActionMessages = val;
    },
    setShowResourceMessages(val: boolean) {
      this.showResourceMessages = val;
    },
    setShowMomentumMessages(val: boolean) {
      this.showMomentumMessages = val;
      useSimulatedRoundStore().visualizationSettings.visibleMomentumMessages = val;
    },
    setAnimationSpeed(val: number) {
      this.animationSpeed = val;
      useSimulatedRoundStore().visualizationSettings.animationSpeed = val;
    },
    setShowActiveMarks(val: boolean) {
      this.showActiveMarks = val;
      useSimulatedRoundStore().visualizationSettings.visibleHighlights = val;
    },

    /**
     * Change the (max) amount of rounds of an existing simulation.
     */
    changeRoundAmount(newAmount: number) {
      if (!this.simulation) {
        console.error("no simulation open");
        return;
      }
      if (this.simulator) {
        this.simulator.setMaxRounds(newAmount);
        useSimulationStore().saveSimulationState(this.simulator);
      } else {
        console.error(
          `no simulator for ${this.simulation.id} open, ${newAmount}`
        );
      }
    },

    /**
     * Change one of the default settings for new simulations
     */
    setNewSimulationSetting<K extends keyof NewSimulationSettings>(
      key: K,
      value: NewSimulationSettings[K]
    ) {
      this.newSimulationSettings[key] = value;
    },

    /**
     * Open or close a simulation quickInfo popup.
     */
    toggleSimulationInfo(
      item: Pick<
        SimulationQuickInfo,
        | "roundIndex"
        | "itemType"
        | "itemId"
        | "itemRoundIndex"
        | "type"
        | "x"
        | "y"
      >
    ) {
      const roundQuickItems = this.openQuickInfos[item.roundIndex] ?? [];
      // if the item is already open, close it
      if (
        roundQuickItems.some(
          (i) =>
            i.itemId == item.itemId &&
            i.itemType == item.itemType &&
            item.itemRoundIndex == i.itemRoundIndex
        )
      ) {
        return this.closeSimulationQuickInfo(item);
      } else {
        return this.openSimulationQuickInfo(item);
      }
    },

    /**
     * Open a quick info item of a simulation action object.
     */
    openSimulationQuickInfo(
      item: Pick<
        SimulationQuickInfo,
        | "roundIndex"
        | "itemType"
        | "itemId"
        | "itemRoundIndex"
        | "type"
        | "x"
        | "y"
      >
    ) {
      if (!this.openQuickInfos[item.roundIndex]) {
        this.openQuickInfos[item.roundIndex] = [];
      }

      // if the item is already open, don't open it again
      if (
        this.openQuickInfos[item.roundIndex].some(
          (i) =>
            i.itemId == item.itemId &&
            i.itemType == item.itemType &&
            i.itemRoundIndex == item.itemRoundIndex
        )
      ) {
        return;
      }

      const newItem: SimulationQuickInfo = {
        active: false,
        ...item,
        lastUpdate: Date.now()
      };

      this.openQuickInfos[item.roundIndex].push(newItem);
    },

    /**
     * Close a simulation quickinfo item by removing it from the list.
     */
    closeSimulationQuickInfo(
      item: Pick<
        SimulationQuickInfo,
        "roundIndex" | "itemType" | "itemId" | "type"
      > & { itemRoundIndex?: SimulationQuickInfo["itemRoundIndex"] }
    ) {
      if (!this.openQuickInfos[item.roundIndex]) {
        return;
      }
      const index = this.openQuickInfos[item.roundIndex].findIndex(
        (i) =>
          item.type === i.type &&
          item?.itemId === i?.itemId &&
          item?.itemType === i?.itemType &&
          (item?.itemRoundIndex != null
            ? item?.itemRoundIndex == i.itemRoundIndex
            : true)
      );
      if (index > -1) {
        this.openQuickInfos[item.roundIndex].splice(index, 1);
      }
    },

    /**
     * Change data of an open quickinfo item
     */
    updateSimulationQuickInfo(item: {
      type: SimulationQuickInfo["type"];
      roundIndex: SimulationQuickInfo["roundIndex"];
      itemType?: SimulationQuickInfo["itemType"];
      itemId?: SimulationQuickInfo["itemId"];
      itemRoundIndex?: SimulationQuickInfo["itemRoundIndex"];
    }) {
      const roundQuickItems = this.openQuickInfos[item.roundIndex];
      if (!roundQuickItems) {
        return;
      }
      const index = roundQuickItems.findIndex(
        (i) =>
          item.type === i.type &&
          item.itemId === i.itemId &&
          item.itemType === i.itemType &&
          item.itemRoundIndex == i.itemRoundIndex
      );
      if (index < 0) {
        return;
      }
      Object.assign(roundQuickItems[index], item);
    },

    updateSimulationInstance(simulationData: Partial<SimulationItem>) {
      if (this.simulator == null) return;
      Object.assign(this.simulator, simulationData);
    },

    /**
     *
     */
    setRoundDrawerOpen(roundIndex: number, open: boolean) {
      this.openRoundDrawers[roundIndex] = open;
    },

    showUnfulfilledActionPopup(data: {
      actionId: number;
      maxOccurrencesReached: boolean;
      conditionIds: number[];
    }) {
      this.unfulfilledActionPopup = data;

      return waitUntil(() => this.unfulfilledActionPopup == null);
    },
    hideUnfulfilledActionPopup() {
      this.unfulfilledActionPopup = null;
    },

    showScenarioEndPopup(data: { scenarioEndId: number }) {
      this.scenarioEndPopup = data;

      return waitUntil(() => this.scenarioEndPopup == null);
    },
    hideScenarioEndPopup() {
      this.scenarioEndPopup = null;
    },

    set(options: object) {
      this.$patch((state) => {
        Object.assign(state, options);
      });
    },
    reset() {
      this.$reset();
    }
  }
});

/**
 * Add the predicted values of all flow branches at the end of the round.
 * @param elementValues 
 * @param parseContext 
 * @param impulses 
 * @param openRoundEffects 
 * @returns 
 */
function withCalculatedFlowBranches(
  elementValues: { [uid: string]: ExpressionValue },
  parseContext: WithRequired<ExpressionParseContext, "indices">,
  impulses: RoundImpulses,
  openRoundEffects: {
    target: { [targetId: number]: BaseEffectItem[] };
    resource: { [resourceId: number]: BaseEffectItem[] };
  }
) {
  try {
    const impulsesWithoutFlow = removeFlowImpulses(impulses);
    
    // add any open effects or impulses to the element values
    const updatedElementValues = withOpenEffects(
      elementValues,
      impulsesWithoutFlow,
      openRoundEffects,
      parseContext
    );

    // remove index and flow values from the element values
    for (const _uid in updatedElementValues) {
      const uid = _uid as ValueItemUid;
      if (uid.startsWith("index_") || uid.startsWith("flowBranch_") || uid.startsWith("flow_")) {
        delete updatedElementValues[uid];
      }
    }
    
    const lastRound = parseContext.rounds?.[parseContext.rounds.length - 1] ?? {};
    const updatedParseContext = {
      ...parseContext,
      rounds: [...parseContext.rounds?.slice(0, -1) ?? [], { ...lastRound, elementValues: updatedElementValues }]
    };
    
    // recalculate all indices with the new values
    const indexValues = calculateExpressionValues(parseContext.indices, updatedParseContext);
    for (const _uid in indexValues) {
      const uid = _uid as Uid<"index">;
      updatedElementValues[uid] = indexValues[uid];
    }
    
    const branchesByFlow = useFlowBranchStore().byFlow;
    const flowsWithExpressions = useFlowStore().withExpressions;
    const flows = filterConnectedFlows(flowsWithExpressions, branchesByFlow, elementValues);
    const flowsByUid = useFlowStore().byUid;
    
    const roundIndex = updatedParseContext.roundIndex ?? 0;
    
    const flowValues = calculateExpressionValues(flows, updatedParseContext);
    for (const _flowUid in flowValues) {
      const flowUid = _flowUid as ValueItemUid;
      if (!flowUid.startsWith("flow")) continue;
      
      // filter out the flows that are not active in this round
      const flow = flowsByUid[flowUid];
      if (flow.interval) {
        if ((roundIndex + 1) % flow.interval != 0) {
          elementValues[flowUid] = {
            ...flowValues[flowUid],
            value: 0
          };
          updatedElementValues[flowUid] = {
            ...flowValues[flowUid],
            value: 0
          };
          continue;
        }
      }
      
      elementValues[flowUid] = {
        ...flowValues[flowUid]
      };
      updatedElementValues[flowUid] = {
        ...flowValues[flowUid]
      };
    }
    
    const calculatedFlowBranches = calculateFlowBranches(
      flows,
      branchesByFlow,
      updatedElementValues
    );
    for (const branch of calculatedFlowBranches.values()) {
      const branchUid = branch.uid;
      if (!elementValues[branchUid]) {
        elementValues[branchUid] = {
          value: 0,
          minValue: 0,
          maxValue: 0,
          unit: "",
          unitValid: true,
          valid: true
        };
      }
      elementValues[branchUid].value = branch.value;
    }
    
    return elementValues;
  } catch (e) {
    console.error(e);
    return elementValues;
  }
}

/**
 * Filter out flows that are not connected to any element with a value.
 */
function filterConnectedFlows(flows: Record<number, FlowItem>, branchesByFlow: { [flowId: number]: FlowBranchItem[] }, elementValues: { [uid: string]: ExpressionValue }) {
  const connectedFlows: Record<number, FlowItem> = {};
  for (const flow of Object.values(flows)) {
    const branches = branchesByFlow[flow.id] || [];
    let isConnected = false;
    for (const branch of branches) {
      const elementUid = `${branch.elementType}_${branch.elementId}`;
      if (elementValues[elementUid]) {
        isConnected = true;
        break;
      }
    }
    if (isConnected) {
      connectedFlows[flow.id] = flow;
    }
  }
  return connectedFlows;
}

function withOpenEffects(
  elementValues: { [uid: ValueItemUid]: ExpressionValue },
  impulses: RoundImpulses,
  openRoundEffects: {
    target: { [targetId: number]: BaseEffectItem[] };
    resource: { [resourceId: number]: BaseEffectItem[] };
  },
  parseContext: ExpressionParseContext
) {
  const updatedElementValues = cloneDeep(elementValues);

  try {
    for (const _uid in elementValues) {
      const uid = _uid as ValueItemUid;
      const { type, id } = splitUid(uid);
      const t = type as "target" | "resource";

      const elementValue = updatedElementValues[uid];

      const valueBefore = Number(elementValue.value);
      let valueAfter = Number(valueBefore);

      const effects = (openRoundEffects?.[t]?.[id] ?? []).slice();
      const impulse = impulses?.[t]?.[id];

      if (effects.length == 0) {
        if (impulse) {
          if (impulse?.valueToSet) {
            valueAfter = impulse.valueToSet;
          }
          valueAfter += impulse.pointsToAdd || 0;
          elementValue.value = clampNumber(
            valueAfter,
            elementValue.minValue,
            elementValue.maxValue
          );
        }
        continue;
      }

      if (impulse) {
        const impulseValueEffect = valueToEffect(impulse.pointsToAdd || 0, {
          activatorId: -1,
          activatorType: "scheduledEffect",
          affectedType: t,
          affectedId: id
        });

        effects.push(impulseValueEffect);
      }

      const combinedEffect = effects.reduce((combined, effect) => {
        return addEffects(combined, effect);
      });

      const expressionString = combinedEffect.impulseExpressions[0];

      if (expressionString.startsWith("=")) {
        const calculated = calculateExpression(expressionString, parseContext);
        valueAfter = Number(calculated.value);
        elementValue.value = clampNumber(
          valueAfter,
          elementValue.minValue,
          elementValue.maxValue
        );
        continue;
      }

      const isRandom = expressionString.includes("rand");

      if (isRandom) {
        const calculated = calculateExpression(expressionString, parseContext);
        const min = Number(calculated.minValue);
        const max = Number(calculated.maxValue);
        valueAfter = valueAfter + Math.random() * (max - min) + min;
        elementValue.value = clampNumber(
          valueAfter,
          elementValue.minValue,
          elementValue.maxValue
        );
        continue;
      }

      const calculated = calculateExpression(expressionString, parseContext);
      valueAfter += Number(calculated.value);
      elementValue.value = clampNumber(
        valueAfter,
        elementValue.minValue,
        elementValue.maxValue
      );
      continue;
    }
  } catch (e) {
    console.error(e);
  }

  return updatedElementValues;
}

function removeFlowImpulses(impulses: RoundImpulses) {
  const updatedImpulses = cloneDeep(impulses);
  for (const type in updatedImpulses) {
    for (const id in updatedImpulses[type as "target" | "resource"]) {
      const impulse = updatedImpulses[type as "target" | "resource"][id];
      impulse.sourceValues = impulse.sourceValues.filter(
        (sourceValue) => sourceValue.type != "flowBranch"
      );
      impulse.pointsToAdd = impulse.sourceValues.reduce((sum, sourceValue) => sum + (sourceValue.value || 0), 0);
    }
  }
  return updatedImpulses;
}

/**
 * Clone an object but only with the data properties (no getters).
 * @param obj 
 * @returns 
 */
function cloneWithoutGetters<T extends object>(obj: T) {
  const descriptors = Object.getOwnPropertyDescriptors(obj);

  const dataDescriptors = Object.fromEntries(
    Object.entries(descriptors).filter(
      ([_, desc]) => 'value' in desc
    )
  );

  return Object.defineProperties({}, dataDescriptors);
}

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      useSimulationViewStore as any,
      import.meta.hot
    )
  );
}
